/**
 * Talking to the FlowLab backend.
 *
 * The page is served with its configuration inlined by
 * `backend/flowlab/api/viewer.py`, so nothing here has to guess endpoints. Two
 * pages are served that way and they carry the same shape: "/" with no
 * Generation chosen, and `/api/generations/{id}/viewer` with one pinned. In
 * `npm run dev` the element is absent and the fallbacks below let the workspace
 * run against `?generation_id=`, or against nothing at all.
 *
 * The client speaks the canonical vocabulary: an EngineeringModel by id and
 * version, a Generation by id. What it replaces spoke of a job, and built its
 * own URLs under `/mesh/` from a `jobId` -- a MeshJob row that no longer exists.
 *
 * **No function here names an artifact.** A Generation has exactly one
 * renderable artifact, resolved server-side from its own `mesh_ref`, so the
 * surface and download URLs take no path segment from this file and there is
 * nothing a caller could redirect.
 *
 * **Creating a Generation is not starting a job**, and this file is where that
 * distinction is kept honest. `createGeneration` returns either an existing
 * Generation (200 -- identical inputs are not remeshed) or an acknowledgement
 * carrying the input hash (202). The hash is the key the caller then polls on:
 * the dedup key and the poll key are the same value, which is why there is no
 * job id to track and nothing that resembles a queue.
 */

const CONFIG_ELEMENT_ID = 'flowlab-viewer-config';

const DEFAULT_API = {
  models: '/api/models',
  geometry: '/api/geometry',
  generations: '/api/generations',
};

/** Read the configuration the backend inlined into the page. */
export function readConfig() {
  const element = document.getElementById(CONFIG_ELEMENT_ID);
  if (element?.textContent) {
    try {
      return normalise(JSON.parse(element.textContent));
    } catch (cause) {
      throw new Error(`The FlowLab configuration is malformed: ${cause.message}`);
    }
  }

  // Development fallback: vite serves index.html directly, so derive what we
  // can from the query string and let the dev proxy reach the backend. Unlike
  // the viewer this replaces, no generation id is required -- the workspace is
  // a valid place to be with nothing selected. `?fresh=1` starts empty even
  // when a pinned id or history exists, for manual local testing.
  const params = new URLSearchParams(window.location.search);
  const fresh = params.get('fresh');
  if (fresh === '1' || fresh === 'true') {
    return { ...normalise({}), fresh: true };
  }
  return { ...normalise({ generationId: params.get('generation_id') }), fresh: false };
}

/** The per-Generation endpoints, from an id alone. */
export function endpointsFor(generationId) {
  const base = `/api/generations/${generationId}`;
  return {
    generation: base,
    artifacts: `${base}/artifacts`,
    surface: `${base}/surface`,
    download: `${base}/mesh`,
  };
}

function normalise(config) {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const freshParam = params?.get('fresh');
  const fresh = freshParam === '1' || freshParam === 'true';
  // `?fresh=1` is a local-testing affordance only: start with nothing selected
  // even when the backend inlines a pinned Generation or lists history. The
  // rail still lists history below, so no API behaviour changes.
  const generationId = fresh ? null : config.generationId || null;
  return {
    generationId,
    fresh,
    status: config.status || '',
    // The mesh's content address, and the structured error a failed Generation
    // carries. Both are canonical fields, passed through rather than reshaped.
    meshRef: config.meshRef || null,
    error: config.error || null,
    wasmBundleUrl: config.wasmBundleUrl || import.meta.env?.VITE_VTK_WASM_BUNDLE_URL || '',
    // Null when no Generation is pinned, rather than URLs built around a null
    // id: those would resolve to nothing, and a declared endpoint should exist.
    endpoints: generationId
      ? { ...endpointsFor(generationId), ...(config.endpoints || {}) }
      : null,
    api: { ...DEFAULT_API, ...(config.api || {}) },
  };
}

/** An HTTP failure carrying the status so callers can explain it precisely. */
export class ApiError extends Error {
  constructor(message, status, body = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    // The structured detail, where the backend sent one. 409 on unresolved
    // bindings names the groups, and that list is the whole content of the
    // message a user needs -- flattening it to a string would throw it away.
    this.body = body;
  }
}

/**
 * Pull the detail out of a FastAPI error response.
 *
 * The backend reports real diagnostics ("generation … produced no mesh") in
 * `detail`, and surfacing that is far more useful than a bare status code.
 */
async function describeFailure(response) {
  let detail = '';
  let body = null;
  try {
    body = await response.json();
    const raw = body?.detail;
    if (typeof raw === 'string') detail = raw;
    else if (raw && typeof raw === 'object') detail = raw.message || raw.reason || '';
  } catch {
    // Not JSON; the status text is the best we have.
  }
  return { detail: detail || `${response.status} ${response.statusText}`, body };
}

async function request(url, { accept, method = 'GET', json, signal } = {}) {
  const headers = {};
  if (accept) headers.Accept = accept;
  if (json !== undefined) headers['Content-Type'] = 'application/json';

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: Object.keys(headers).length ? headers : undefined,
      body: json === undefined ? undefined : JSON.stringify(json),
      signal,
    });
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    // fetch() only rejects on network-level failures.
    throw new ApiError(`Could not reach the FlowLab backend at ${url}`, 0);
  }
  if (!response.ok) {
    const { detail, body } = await describeFailure(response);
    throw new ApiError(detail, response.status, body?.detail ?? body);
  }
  return response;
}

async function getJson(url, options) {
  const response = await request(url, { accept: 'application/json', ...options });
  return response.json();
}

function query(parameters) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

/* ------------------------------------------------------------------ models */

/** Every model, one entry per model id at its highest version. */
export async function listModels(config, { signal } = {}) {
  const data = await getJson(config.api.models, { signal });
  return data.models || [];
}

/**
 * Derive the geometry inventory for a model.
 *
 * POST /api/models/{id}/inventory takes no body: the server measures the
 * geometry its stored model already names and appends a new model version
 * carrying the measured provenance. The envelope is returned verbatim, so the
 * caller learns the new version (and its snapshot hash) exactly as authored.
 */
export async function deriveInventory(config, modelId, { signal } = {}) {
  const response = await request(`${config.api.models}/${modelId}/inventory`, {
    method: 'POST',
    accept: 'application/json',
    signal,
  });
  return response.json();
}

/** One model, at its current version or at `version`. */
export async function getModel(config, modelId, { version, signal } = {}) {
  const url = `${config.api.models}/${modelId}${query({ version })}`;
  return getJson(url, { signal });
}

/** The version history of one model: newest first, hashes included. */
export async function listModelVersions(config, modelId, { signal } = {}) {
  const data = await getJson(`${config.api.models}/${modelId}/versions`, { signal });
  return data.versions || [];
}

/**
 * Author version one of an EngineeringModel from a complete canonical draft.
 *
 * Identity and version remain server-owned: callers provide only the authored
 * fields accepted by ``ModelDraft`` and receive the canonical envelope back.
 */
export async function createModel(config, draft, { signal } = {}) {
  const response = await request(config.api.models, {
    method: 'POST',
    accept: 'application/json',
    json: draft,
    signal,
  });
  return response.json();
}

/* ------------------------------------------------------------- generations */

/**
 * Generations, newest first, filtered by any canonical field.
 *
 * `model_snapshot_hash` is how a model's history is read: a Generation
 * references the *snapshot* it was built from, not the model row, so this is
 * the query that answers "what has been generated from this version".
 */
export async function listGenerations(config, filters = {}, { signal } = {}) {
  const url = `${config.api.generations}${query(filters)}`;
  const data = await getJson(url, { signal });
  return data.generations || [];
}

/** One Generation, whole. */
export async function getGeneration(config, generationId, { signal } = {}) {
  return getJson(`${config.api.generations}/${generationId}`, { signal });
}

/**
 * Ask for a Generation of `configuration` from a model version.
 *
 * Returns `{existing, generation}` when the platform already holds a Generation
 * for these exact inputs (200), and `{existing: false, inputHash, ...}` when the
 * request was accepted for execution (202). The caller does not get a handle to
 * anything running -- there is nothing running that it could name. It gets the
 * content key of the result it asked for, which will identify that result
 * whenever it exists.
 */
export async function createGeneration(config, { modelId, version, configuration }, options = {}) {
  const response = await request(config.api.generations, {
    method: 'POST',
    accept: 'application/json',
    json: { model_id: modelId, version: version ?? null, configuration },
    signal: options.signal,
  });
  const body = await response.json();
  if (response.status === 200) {
    return { existing: true, generation: body, inputHash: body?.generation?.input_hash || null };
  }
  return {
    existing: false,
    generation: null,
    inputHash: body.input_hash,
    modelSnapshotHash: body.model_snapshot_hash,
    version: body.version,
  };
}

/**
 * Wait for the Generation keyed by `inputHash` to exist.
 *
 * Polling the dedup key rather than a task id is the whole reason no job object
 * is needed: the key was computed before dispatch from the inputs alone, so it
 * identifies the result from the moment it is asked for. A `failed` Generation
 * satisfies this too -- it is a result, and the caller displays its error.
 */
export async function awaitGeneration(
  config,
  inputHash,
  { signal, intervalMs = 1500, timeoutMs = 20 * 60 * 1000, onTick } = {},
) {
  const deadline = Date.now() + timeoutMs;
  let attempt = 0;
  for (;;) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const found = await listGenerations(config, { input_hash: inputHash, limit: 1 }, { signal });
    if (found.length > 0) return found[0];
    if (Date.now() >= deadline) {
      throw new ApiError(
        'The mesh is taking longer than expected. It will appear in the history when it resolves.',
        0,
      );
    }
    attempt += 1;
    onTick?.(attempt);
    await sleep(intervalMs, signal);
  }
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/* ---------------------------------------------------------------- geometry */

/** Ingest a CAD file. The response is the content address and its provenance. */
export async function uploadGeometry(config, file, { signal } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);
  let response;
  try {
    response = await fetch(config.api.geometry, {
      method: 'POST',
      body: form,
      headers: { Accept: 'application/json' },
      signal,
    });
  } catch (cause) {
    if (cause?.name === 'AbortError') throw cause;
    throw new ApiError('Could not reach the FlowLab backend', 0);
  }
  if (!response.ok) {
    const { detail, body } = await describeFailure(response);
    throw new ApiError(detail, response.status, body?.detail ?? body);
  }
  return response.json();
}

/* --------------------------------------------------------------- artifacts */

/**
 * List what this Generation can display: one artifact, or none.
 *
 * A list of at most one, so "nothing to render" and "one thing to render" are
 * the same shape and a failed Generation needs no special case here.
 */
export async function fetchArtifacts(config) {
  const response = await request(config.endpoints.artifacts, { accept: 'application/json' });
  const data = await response.json();
  return {
    artifacts: data.artifacts || [],
    default: data.default || null,
  };
}

/**
 * Fetch the Generation's surface as raw bytes.
 *
 * @param {object} config
 * @returns {Promise<ArrayBuffer>}
 */
export async function fetchSurface(config, { signal } = {}) {
  const response = await request(config.endpoints.surface, {
    accept: 'application/vnd.flowlab.mesh',
    signal,
  });
  return response.arrayBuffer();
}

/** The URL a user can hit to download the canonical mesh. */
export function downloadUrl(config) {
  return config.endpoints.download;
}
